import { useEffect } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import Header from './components/header/Header';
import FrameSection from './components/frameSection/FrameSection';
import ErrorFallback from './components/ErrorFallback/ErrorFallback';
import Logger from './helpers/Log';
import { Outlet, useLocation } from 'react-router-dom';
import { MobileSidebarProvider } from './contexts/MobileSidebar/MobileSidebarContext';
import useAuthContext from './contexts/Auth/useAuthContext';
// The Sprints widget ("דיווח טכני") is the single reporting entry point for all
// users — the internal feedback button was removed in its favor. FeedbackModal
// itself remains (used by OldSiteExitDialog), as does the management page.
import SprintsReportWidget from './components/SprintsReportWidget/SprintsReportWidget';
import SystemMessagesPopup from './components/SystemMessages/SystemMessagesPopup';
import RegulationsAcceptanceDialog from './components/Regulations/RegulationsAcceptanceDialog';
import { initConsoleCapture, initNetworkCapture } from './utils/feedbackCapture';
import TaskSatisfactionPrompt from './components/adoption/TaskSatisfactionPrompt';
import { AnnouncementsProvider } from './components/Announcements/AnnouncementsContext';
import AnnouncementTour from './components/Announcements/AnnouncementTour';
import { AnnouncementImageViewer } from './components/Announcements/AnnouncementImage';

/* מוגדר מחוץ ל-Layout: FallbackComponent חדש בכל render היה מאפס את הכרטיסייה. */
const RouteErrorFallback = ({ error }: FallbackProps) => <ErrorFallback error={error} inline />;

const logRouteError = (error: Error) => Logger.error('Unexpected error caught by route ErrorBoundary', error);

export default function Layout() {
  const { currentUser } = useAuthContext();
  const { pathname } = useLocation();

  useEffect(() => {
    // Console/network capture still feeds FeedbackModal (OldSiteExitDialog)
    initConsoleCapture();
    initNetworkCapture();
  }, []);

  return (
    <MobileSidebarProvider>
      {/* The feature announcements' chips and bubbles (HAT-1489): above the header, the fund routes and
          the management area alike, since all three show them. */}
      <AnnouncementsProvider>
        <Header />
        <FrameSection>
          {/*
            HAT-1343: עד כאן היה ErrorBoundary אחד בלבד, ב-main.tsx, **מעל** ה-Router.
            כל שגיאת render בעמוד כלשהו החליפה בגללו את האפליקציה כולה - גם התפריט
            וגם הכותרת - בכרטיסיית שגיאה בודדת, ולמשתמש לא נשאר על המסך דבר ללחוץ
            עליו ("משהו לא הגיב ללחיצות"). הגבול הזה עוצר את השגיאה באזור התוכן,
            כך שהניווט נשאר זמין.

            resetKeys על pathname: מעבר למסך אחר מנקה את השגיאה מעצמו. זה המוצא
            שחסר קודם - reset בלי שינוי כלשהו רק מרנדר מחדש את אותו מסך שבור.
          */}
          <ErrorBoundary FallbackComponent={RouteErrorFallback} onError={logRouteError} resetKeys={[pathname]}>
            <Outlet />
          </ErrorBoundary>
        </FrameSection>
        {currentUser && <SprintsReportWidget />}
        {currentUser && <SystemMessagesPopup />}
        {currentUser && <TaskSatisfactionPrompt />}
        {/* "אישור כללי הקרן" on entry (HAT-1256). Last, so that if it and an announcement land in
            the same moment the rules are the dialog in front. The component decides for itself
            whether this coordinator still has to sign. */}
        {currentUser && <RegulationsAcceptanceDialog />}
        {currentUser && <AnnouncementTour />}
        {currentUser && <AnnouncementImageViewer />}
      </AnnouncementsProvider>
    </MobileSidebarProvider>
  );
}
